/**
 * Streaming helpers for PrecisionAI.
 *
 * The panel asks POST /api/chat for `{ stream: true }` and will render tokens as
 * soon as any of these arrive:
 * - `text/event-stream` (or NDJSON) on the POST itself
 * - GET /api/chat/sessions/:id/stream as SSE
 * - Pusher `chat-updated` / `chat-delta` with `partial`, `delta`, or `token`
 * - session `partial_response` (or a growing assistant message) while status is processing
 *
 * Missing stream endpoints are ignored; the existing job poll still completes the turn.
 */

export function getResponseContentType(response) {
  try {
    return response?.headers?.get?.('content-type') || '';
  } catch {
    return '';
  }
}

export function isEventStreamResponse(response) {
  return /event-stream|ndjson|jsonl/i.test(getResponseContentType(response));
}

export function sessionPartialContent(session) {
  if (!session || typeof session !== 'object') return '';
  for (const key of ['partial_response', 'partial', 'streaming_content', 'draft']) {
    const value = session[key];
    if (typeof value === 'string' && value) return value;
  }
  const messages = Array.isArray(session.messages) ? session.messages : [];
  const last = messages[messages.length - 1];
  if (
    last?.role === 'assistant'
    && typeof last.content === 'string'
    && last.content
    && (last.partial || last.streaming || last.status === 'streaming' || session.status === 'processing')
  ) {
    return last.content;
  }
  return '';
}

export function pickLongerPartial(a = '', b = '') {
  if (!a) return b || '';
  if (!b) return a;
  if (a.startsWith(b) || b.startsWith(a)) return a.length >= b.length ? a : b;
  return a.length >= b.length ? a : b;
}

function userContents(messages) {
  return (Array.isArray(messages) ? messages : [])
    .filter((message) => message?.role === 'user' && typeof message.content === 'string')
    .map((message) => message.content);
}

/**
 * A session poll can land before POST stores the new turn. Keep local user
 * messages the server snapshot does not have yet so the bubble stays visible
 * until the reply arrives.
 */
export function mergeServerMessagesWithPending(serverMessages, localMessages) {
  const server = Array.isArray(serverMessages) ? serverMessages.map((message) => ({ ...message })) : [];
  const localUsers = userContents(localMessages);
  const serverUsers = userContents(server);
  const serverIsPrefix = serverUsers.every((content, index) => localUsers[index] === content);
  if (!serverIsPrefix || localUsers.length <= serverUsers.length) {
    return { messages: server, retainedPending: false };
  }
  const extra = localUsers.slice(serverUsers.length).map((content) => ({ role: 'user', content }));
  return { messages: [...server, ...extra], retainedPending: true };
}

export function mergeMessagesWithPartial(messages, partial, { processing } = {}) {
  const next = Array.isArray(messages) ? messages.map((message) => ({ ...message })) : [];
  if (!processing) {
    return next.map(({ streaming, ...rest }) => rest);
  }
  if (!partial) return next;
  const last = next[next.length - 1];
  if (last?.role === 'assistant') {
    const current = last.content || '';
    const chosen = pickLongerPartial(current, partial);
    next[next.length - 1] = { ...last, content: chosen, streaming: true };
    return next;
  }
  next.push({ role: 'assistant', content: partial, streaming: true });
  return next;
}

export function applyPartialToMessages(messages, partial) {
  return mergeMessagesWithPartial(messages, partial, { processing: true });
}

export function chatStreamEventContent(event, previous = '') {
  if (!event || typeof event !== 'object') return previous;
  if (event.done === true) return previous;
  if (typeof event.partial_response === 'string') return event.partial_response;
  if (typeof event.partial === 'string') return event.partial;
  if (typeof event.content === 'string' && event.delta == null && event.token == null) {
    return event.content;
  }
  if (typeof event.delta === 'string') return previous + event.delta;
  if (typeof event.token === 'string') return previous + event.token;
  return previous;
}

export function chatStreamEventSessionId(event) {
  const id = event?.session_id ?? event?.sessionId;
  return id == null || id === '' ? null : id;
}

function emitSseChunk(chunk, { isNdjson, onEvent }) {
  const trimmed = chunk.trim();
  if (!trimmed) return;
  if (isNdjson) {
    try {
      onEvent?.(JSON.parse(trimmed));
    } catch {
      onEvent?.({ delta: trimmed });
    }
    return;
  }
  const dataLines = trimmed
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).replace(/^ /, ''));
  if (!dataLines.length) return;
  const raw = dataLines.join('\n');
  if (raw === '[DONE]') {
    onEvent?.({ done: true });
    return;
  }
  try {
    onEvent?.(JSON.parse(raw));
  } catch {
    onEvent?.({ delta: raw });
  }
}

export async function consumeSseResponse(response, { onEvent, signal } = {}) {
  if (!response?.body || typeof response.body.getReader !== 'function') return;
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const isNdjson = /ndjson|jsonl/i.test(getResponseContentType(response));
  let buffer = '';

  try {
    while (true) {
      if (signal?.aborted) {
        await reader.cancel();
        return;
      }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const separator = isNdjson ? '\n' : '\n\n';
      const parts = buffer.split(separator);
      buffer = parts.pop() || '';
      parts.forEach((part) => emitSseChunk(part, { isNdjson, onEvent }));
    }
    buffer += decoder.decode();
    if (buffer.trim()) emitSseChunk(buffer, { isNdjson, onEvent });
  } catch (error) {
    if (error?.name === 'AbortError' || signal?.aborted) return;
    throw error;
  }
}
